import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, createBlock as _createBlock, resolveDynamicComponent as _resolveDynamicComponent, toHandlers as _toHandlers, mergeProps as _mergeProps, withCtx as _withCtx, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "application-status-wrapper" }
const _hoisted_2 = { class: "header" }
const _hoisted_3 = { class: "cancel-header" }
const _hoisted_4 = { class: "title" }
const _hoisted_5 = {
  key: 0,
  class: "redirect-button-container"
}
const _hoisted_6 = { class: "application-status" }
const _hoisted_7 = { class: "header" }

import { ref, computed, onMounted, watch } from "vue";
import { useStore } from "vuex";
import { $api } from "@/services/api";
import Tile from "@/components/shared/Tile.vue";
import CancellationModal from "@/components/modals/CancellationModal.vue";
import useFileUtilities from "@/use/fileUtilities";
import Complete from "@/components/application-statuses/Complete.vue";
import Cancelled from "@/components/application-statuses/Cancelled.vue";
import Declined from "@/components/application-statuses/Declined.vue";
import EvaluationQueue from "@/components/application-statuses/EvaluationQueue.vue";
import NotSubmitted from "@/components/application-statuses/NotSubmitted.vue";
import SubmittedAndProcessing from "@/components/application-statuses/SubmittedAndProcessing.vue";
import RightToCancelPeriod from "@/components/application-statuses/RightToCancelPeriod.vue";
import SchoolCertification from "@/components/application-statuses/SchoolCertification.vue";
import CertificationUpdatesTile from "@/components/certification-updates/CertificationUpdatesTile.vue";
import { ApplicationStatusTypeIds, ProductIds } from "@/config/constants";
import type { ApplicationDetail } from "@/models/application";
import useApplicationStatus from "@/composables/application-status/useApplicationStatus";
import Loader from "@/components/shared/Loader.vue";
import { DocumentTypes } from "@/config/enums";
import useApplicationData from "@/composables/useApplicationData";
import SLCRedirectButton from "@/components/buttons/SLCRedirectButton.vue";
import useLoanData from "@/composables/loan/useLoanData";
import useApplicationInfo from "@/composables/application-info/useApplicationInfo";


export default /*@__PURE__*/_defineComponent({
  __name: 'ApplicationStatus',
  props: {},
  emits: ["todoListClicked", "viewCertificationUpdates"],
  setup(__props, { expose: __expose, emit: __emit }) {

const certUpdatesTileRef = ref<InstanceType<typeof CertificationUpdatesTile> | null>(null);

const store = useStore();

const { productTypeId, timeline, currentstep, setCurrentStep } = useApplicationStatus();
const { application } = useApplicationData();
const { base64ToBlob } = useFileUtilities();
const { loan, fundsDisbursed, loanBoarded } = useLoanData();
const { details } = useApplicationInfo();

const applicationDetail = computed<ApplicationDetail>(() => store.state?.applicationDetail);

const showCertificationUpdatesTile = computed<boolean>(() => {
  return details.value?.showCertificationUpdatesTile === true;
});

const certifications = computed(() => {
  return store.state.loanConfigurationModule?.certifications;
});
const emit = __emit;

const props = __props;

// template refs
const fileAccess = ref() as any;

// Data
const docAvailable = ref<boolean | undefined>(undefined);
const gettingData = ref<boolean>(true);

const componentName = computed(() => {
  return getStatusComponent();
});
const currentProperties = computed(() => {
  switch (componentName.value) {
    case Declined:
      return { noticeReady: noticeReady.value };
    default:
      return undefined;
  }
});
const currentListeners = computed(() => {
  if (
    componentName.value === EvaluationQueue ||
    componentName.value === NotSubmitted ||
    componentName.value === RightToCancelPeriod ||
    componentName.value === SubmittedAndProcessing
  ) {
    return {
      todoListClicked: handleToDoListClickedEvent,
    };
  } else if (componentName.value === Declined) {
    return {
      downloadAdverseActionNotice: handleDownloadAdverseActionNoticeEvent,
    };
  } else if (componentName.value === SchoolCertification || componentName.value === Complete) {
    return {
      viewCertificationUpdates: () => {
        emit("viewCertificationUpdates");
      },
    };
  } else {
    return {};
  }
});
const noticeReady = computed(() => (docAvailable.value ? docAvailable.value : false));
const productName = computed(() => store.getters["productDisplayName"]);
const applicationId = computed(() => application.value?.id);
const cancelApplication = computed(() => store.getters["modal/cancellationModalActive"]);
const applicationStatusId = computed(() => store.state.appData?.application?.applicationStatusId);
const showCancellationLink = computed(() => {
  return (
    applicationStatusId.value === ApplicationStatusTypeIds.NOT_SUBMITTED ||
    applicationStatusId.value === ApplicationStatusTypeIds.EVALUATION ||
    applicationStatusId.value === ApplicationStatusTypeIds.SUBMITTED_AND_PROCESSING ||
    applicationStatusId.value === ApplicationStatusTypeIds.RIGHT_TO_CANCEL ||
    applicationStatusId.value === ApplicationStatusTypeIds.SCHOOL_CERTIFICATION
  );
});

const showSLCButtonLOC = computed<boolean>(() => {
  return (
    applicationStatusId.value === ApplicationStatusTypeIds.COMPLETE &&
    productTypeId.value === ProductIds.LOC &&
    fundsDisbursed.value &&
    loanBoarded.value
  );
});
function handleToDoListClickedEvent() {
  if (
    componentName.value === EvaluationQueue ||
    componentName.value === NotSubmitted ||
    componentName.value === RightToCancelPeriod ||
    componentName.value === SubmittedAndProcessing
  ) {
    emit("todoListClicked");
  } else {
    return;
  }
}
function handleDownloadAdverseActionNoticeEvent() {
  if (componentName.value === Declined) {
    downloadAdverseActionNotice();
  } else {
    return;
  }
}

function addDays(date: Date, days: number) {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

function openCancellationModal() {
  store.dispatch("modal/openCancellationModal");
}

function getStatusComponent() {
  switch (applicationStatusId.value) {
    case ApplicationStatusTypeIds.CANCELLED:
      return Cancelled;
    case ApplicationStatusTypeIds.NOT_SUBMITTED:
      return NotSubmitted;
    case ApplicationStatusTypeIds.COMPLETE:
      return Complete;
    case ApplicationStatusTypeIds.DECLINED:
    case ApplicationStatusTypeIds.SMR_DECLINED:
      return Declined;
    case ApplicationStatusTypeIds.EVALUATION:
      return EvaluationQueue;
    case ApplicationStatusTypeIds.SUBMITTED_AND_PROCESSING:
      return SubmittedAndProcessing;
    case ApplicationStatusTypeIds.RIGHT_TO_CANCEL:
      return RightToCancelPeriod;
    case ApplicationStatusTypeIds.SCHOOL_CERTIFICATION:
      return SchoolCertification;
    default:
      return undefined;
  }
}

async function checkForLetter() {
  const notice = await $api.documents.getApplicationDocumentByType(
    applicationId.value,
    Number(DocumentTypes.AdverseAction),
  );

  if (notice && notice.length > 0) {
    const b64 = await $api.documents.getDocument(notice.pop().documentId);

    if (b64) {
      docAvailable.value = true;
    } else {
      docAvailable.value = false;
    }
  }
}
async function downloadAdverseActionNotice() {
  const notice = await $api.documents.getApplicationDocumentByType(applicationId.value, 25);
  const b64 = await $api.documents.getDocument(notice.pop().documentId);

  console.log("b64", b64);
  const blob = base64ToBlob(b64);
  console.log(`blob ${new Date()}`, blob);

  if (blob.size > 0) {
    const blobURL = URL.createObjectURL(blob);
    let a = fileAccess as HTMLAnchorElement;
    // console.log("this.$refs.link", this.$refs.link);
    a.setAttribute("download", "Application.pdf");
    a.setAttribute("href", `${blobURL}`);
    a.click();
    URL.revokeObjectURL(blobURL);
  } else {
    // #TODO do they want messaging if download fails?
    console.log("FAILED TO DOWNLOAD");
  }
}

const applicationStatusModule = computed(() => store.state.applicationStatusModule);

onMounted(async () => {
  gettingData.value = true;

  await store.dispatch("getApplicationDetails", applicationId.value);

  await store.dispatch("applicationStatusModule/getTimelineAsync", {
    applicationId: applicationId.value,
    applicantId: store.state.appData?.currentApplicant?.id,
  });

  setCurrentStep(applicationStatusModule.value.selectedStep);

  if (
    applicationStatusId.value === ApplicationStatusTypeIds.DECLINED ||
    applicationStatusId.value === ApplicationStatusTypeIds.SMR_DECLINED
  ) {
    await checkForLetter();
  }

  gettingData.value = false;
});

watch(
  () => applicationStatusId.value,
  async (value: any) => {
    if (value) {
      if (
        applicationStatusId.value === ApplicationStatusTypeIds.DECLINED ||
        applicationStatusId.value === ApplicationStatusTypeIds.SMR_DECLINED
      ) {
        await checkForLetter();
      }
    }
  },
);

__expose({
  timeline,
  $refs: {
    certUpdatesTileRef,
  },
  currentstep,
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(Tile, null, {
      default: _withCtx(() => [
        _createElementVNode("div", _hoisted_1, [
          _createElementVNode("div", _hoisted_2, [
            _createElementVNode("div", _hoisted_3, [
              _createElementVNode("p", _hoisted_4, _toDisplayString(productName.value), 1),
              (showCancellationLink.value && _unref(productTypeId) === _unref(ProductIds).PEL)
                ? (_openBlock(), _createElementBlock("a", {
                    key: 0,
                    class: "cancellation-link",
                    onClick: openCancellationModal
                  }, "Cancel Application"))
                : _createCommentVNode("", true)
            ]),
            _createElementVNode("p", null, "Application ID: " + _toDisplayString(applicationDetail.value.referenceId), 1),
            _createElementVNode("p", null, _toDisplayString(_unref(application).created ? `Started on ${applicationDetail.value.createdAt}` : "Started on") + " | " + _toDisplayString(applicationDetail.value.expiredAt ? `Expires on ${applicationDetail.value.expiredAt}` : "Expires on"), 1)
          ]),
          (showSLCButtonLOC.value && _unref(productTypeId) === _unref(ProductIds).LOC)
            ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                _createVNode(SLCRedirectButton, { openInNewTab: false })
              ]))
            : _createCommentVNode("", true),
          _createElementVNode("div", _hoisted_6, [
            _createElementVNode("div", _hoisted_7, [
              _cache[0] || (_cache[0] = _createElementVNode("p", null, "Application Status", -1)),
              (
              showCancellationLink.value &&
              (_unref(productTypeId) === _unref(ProductIds).REFI || _unref(productTypeId) === _unref(ProductIds).LOC)
            )
                ? (_openBlock(), _createElementBlock("a", {
                    key: 0,
                    class: "cancellation-link",
                    onClick: openCancellationModal
                  }, "Cancel Application"))
                : _createCommentVNode("", true),
              (cancelApplication.value)
                ? (_openBlock(), _createBlock(CancellationModal, {
                    key: 1,
                    application: _unref(application)
                  }, null, 8, ["application"]))
                : _createCommentVNode("", true)
            ]),
            (gettingData.value)
              ? (_openBlock(), _createBlock(Loader, { key: 0 }))
              : (_openBlock(), _createBlock(_resolveDynamicComponent(componentName.value), _mergeProps({ key: 1 }, currentProperties.value, _toHandlers(currentListeners.value)), null, 16))
          ])
        ])
      ]),
      _: 1
    }),
    (showCertificationUpdatesTile.value)
      ? (_openBlock(), _createBlock(CertificationUpdatesTile, {
          key: 0,
          ref_key: "certUpdatesTileRef",
          ref: certUpdatesTileRef,
          id: "certification-updates",
          certifications: certifications.value,
          loading: gettingData.value
        }, null, 8, ["certifications", "loading"]))
      : _createCommentVNode("", true)
  ], 64))
}
}

})